import { DateField } from '@flos/react-ui';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import { useHippoText } from '../../../../hooks/useHippoText';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { type CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { useState } from 'react';
import { convertDateToLocalDateDTO, convertLocalDateDTOToDate } from '../../../../utils/dateUtil';
import ViewInformationModal from '../../../../shared_components/ViewInformationModal/ViewInformationModal';
import { getCoverableFromAllOnsaCoverables } from '../../../../utils/coverablesUtil';
import RemoveModal from '../../../../shared_components/RemoveModal/RemoveModal';
import { track } from '../../../../utils/trackingUtil';
import HelpLink from '../../../../shared_components/HelpLink/HelpLink';

const YoungContentDobStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [coverables, setCoverables] = useRecoilState(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const contentCoverables = coverables.youthContent ?? [];
    const coverableIndex = contentCoverables?.findIndex(
        (coverable) => coverable.fixedId === currentProduct.fixedId
    );
    const currentContentCoverable = contentCoverables[coverableIndex];
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const currentCoverable = getCoverableFromAllOnsaCoverables(
        coverables,
        currentProduct.fixedId,
        currentProduct.lobCode
    );

    const viewInformationModalContent = {
        title: getHippoText('DSS-CON -HIGHLIGHTED-TEXT-MODAL-BIRTHDAY-PRC-HEADER'),
        description: getHippoText('DSS-CON -HIGHLIGHTED-TEXT-MODAL-BIRTHDAY-YOUNGPRC')
    };

    const validateBirthdate = (birthdate: Date): boolean => {
        const twentyEightYearsLater = new Date(
            birthdate.getFullYear() + 28,
            birthdate.getMonth(),
            birthdate.getDate()
        );
        const today = new Date();
        const fortyDaysLater = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() + 40
        );
        return twentyEightYearsLater > fortyDaysLater;
    };

    const handleBirthDateChange = (date: Date): void => {
        const updatedCoverables = [...contentCoverables];
        updatedCoverables[coverableIndex] = {
            ...updatedCoverables[coverableIndex],
            dobOfOldestInHousehold: convertDateToLocalDateDTO(date)
        };

        setCoverables((prev) => ({
            ...prev,
            youthContent: updatedCoverables
        }));
    };

    const onNext = (): void => {
        setIsLoading(true);
        const validBirthdate = validateBirthdate(
            convertLocalDateDTOToDate(currentContentCoverable?.dobOfOldestInHousehold ?? {})
        );
        if (!validBirthdate) {
            setShowRemoveModal(true);
            setIsLoading(false);
            return;
        }

        const updatedCoverable: CoverableInformationToUpdateDTO = {
            youngContent: {
                publicId: currentContentCoverable.publicId,
                fixedId: currentContentCoverable.fixedId,
                dobOfOldestInHousehold: currentContentCoverable.dobOfOldestInHousehold
            },
            productToUpdate: currentProduct.flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(() => {
            track(`youthContentCoverableInformationDateOfBirthOfOldestInHousehold`, [
                { dobOfOldestInHousehold: currentContentCoverable?.dobOfOldestInHousehold }
            ]);
            goNext();
            setIsLoading(false);
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={isLoading || !currentContentCoverable.dobOfOldestInHousehold}
            disablePrevious={isLoading}
            isNextLoading={isLoading}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-BIRTHDAY-HEADER')}
            >
                <DateField
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP- BIRTHDAY-INPUTFIELD')}
                    onChange={handleBirthDateChange}
                    selected={
                        currentContentCoverable.dobOfOldestInHousehold
                            ? convertLocalDateDTOToDate(
                                  currentContentCoverable.dobOfOldestInHousehold
                              )
                            : undefined
                    }
                    maxDate={new Date()}
                    disabled={isLoading}
                />
                <HelpLink
                    onClick={() => {
                        setShowModal(true);
                    }}
                >
                    {getHippoText('DSS-COVERABLE-INFORMATION-STEP- BIRTHDAY-WHY-DO-YOU-ASK')}
                </HelpLink>
            </CoverableDetailsTemplate>
            <ViewInformationModal
                showModal={showModal}
                modalProps={viewInformationModalContent}
                closeModal={() => {
                    setShowModal(false);
                }}
            />
            {showRemoveModal && currentCoverable && (
                <RemoveModal
                    coverable={currentCoverable}
                    title={getHippoText('DSS-REMOVE-CONTINUE-MODAL-OTHER-CONTENT-HEADER')}
                    description={getHippoText('DSS-REMOVE-CONTINUE-MODAL-OTHER-CONTENT-SUBHEADER')}
                    isRemoveAndContinueModal
                    showModal={showRemoveModal}
                    closeModal={() => {
                        setShowRemoveModal(false);
                    }}
                    showCloseButton
                    buttonText={getHippoText(
                        'DSS-REMOVE-CONTINUE-MODAL-YOUNG-CONTENT-REMOVE-BUTTON-TEXT'
                    )}
                />
            )}
        </WizardPagetemplate>
    );
};

export default YoungContentDobStep;
